import { useState, useCallback, useEffect } from 'react'
import Editor from '@monaco-editor/react'
import { FileCode, Copy, Download, Check, Wand2 } from 'lucide-react'
import { Button } from './button'
import { Badge } from './badge'

// Language detection based on file extension
const detectLanguage = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase()
  
  const languageMap: Record<string, string> = {
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'typescript',
    'tsx': 'typescript',
    'py': 'python',
    'java': 'java',
    'cpp': 'cpp',
    'c': 'c',
    'css': 'css',
    'scss': 'scss',
    'sass': 'sass',
    'html': 'html',
    'htm': 'html',
    'xml': 'xml',
    'json': 'json',
    'yaml': 'yaml',
    'yml': 'yaml',
    'md': 'markdown',
    'markdown': 'markdown',
    'sql': 'sql',
    'sh': 'shell',
    'bash': 'shell',
    'php': 'php',
    'rb': 'ruby',
    'go': 'go',
    'rs': 'rust',
    'swift': 'swift',
    'kt': 'kotlin',
    'dart': 'dart',
    'vue': 'vue',
    'svelte': 'svelte'
  }
  
  return languageMap[ext || ''] || 'plaintext'
}

// Format file size
const formatFileSize = (content: string): string => {
  const bytes = new Blob([content]).size
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Count lines
const countLines = (content: string): number => {
  return content.split('\n').length
}

// Format JSON content
const formatJSON = (content: string): string => {
  try {
    const parsed = JSON.parse(content)
    return JSON.stringify(parsed, null, 2)
  } catch (error) {
    return content // Return original if parsing fails
  }
}

// Check if content is valid JSON
const isValidJSON = (content: string): boolean => {
  try {
    JSON.parse(content)
    return true
  } catch {
    return false
  }
}

interface CodeViewerProps {
  content: string
  filename: string
  language?: string
  onDownload?: () => void
  onContentChange?: (newContent: string) => void
  className?: string
  showHeader?: boolean
  allowFormatting?: boolean
}

export function CodeViewer({ 
  content, 
  filename, 
  language: propLanguage, 
  onDownload,
  onContentChange,
  className = '',
  showHeader = true,
  allowFormatting = true
}: CodeViewerProps) {
  const [copied, setCopied] = useState(false)
  const [theme, setTheme] = useState<'vs-dark' | 'light'>('light')
  const [displayContent, setDisplayContent] = useState(content)
  
  const detectedLanguage = propLanguage || detectLanguage(filename)
  const lineCount = countLines(displayContent)
  const fileSize = formatFileSize(displayContent)
  const isJSON = detectedLanguage === 'json'
  const canFormat = isJSON && allowFormatting && isValidJSON(content)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }, [displayContent])

  const handleFormat = useCallback(() => {
    if (canFormat) {
      const formatted = formatJSON(content)
      setDisplayContent(formatted)
      onContentChange?.(formatted)
    }
  }, [content, canFormat, onContentChange])

  const toggleTheme = () => {
    setTheme(prev => prev === 'vs-dark' ? 'light' : 'vs-dark')
  }

  // Reset display content when original content changes
  useEffect(() => {
    setDisplayContent(content)
  }, [content])

  // Render action buttons for external use
  const ActionButtons = () => (
    <div className="flex gap-1 flex-shrink-0">
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={toggleTheme}
        className="h-7 px-2"
        title={`Switch to ${theme === 'vs-dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'vs-dark' ? '☀️' : '🌙'}
      </Button>
      
      {canFormat && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleFormat}
          className="h-7 px-2"
          title="Format JSON"
        >
          <Wand2 className="h-3 w-3" />
        </Button>
      )}
      
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleCopy}
        className="h-7 px-2"
        title="Copy code"
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>
      
      {onDownload && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onDownload}
          className="h-7 px-2"
          title="Download file"
        >
          <Download className="h-3 w-3" />
        </Button>
      )}
    </div>
  )

  return (
    <div className={`h-full flex flex-col ${className}`}>
      {/* Header with file info and actions - conditionally rendered */}
      {showHeader && (
        <div className="flex items-center justify-between p-3 border-b bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <FileCode className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="font-medium text-sm truncate">{filename}</span>
            <Badge variant="secondary" className="text-xs">
              {detectedLanguage.toUpperCase()}
            </Badge>
          </div>
          
          <ActionButtons />
        </div>
      )}

      {/* Monaco Editor */}
      <div className="flex-1 relative overflow-hidden bg-gray-50 border">
        <Editor
          language={detectedLanguage}
          value={displayContent}
          theme={theme}
          options={{
            readOnly: true,
            minimap: { 
              enabled: lineCount > 50 // Only show minimap for larger files
            },
            scrollBeyondLastLine: false,
            fontSize: 12,
            fontFamily: 'JetBrains Mono, Monaco, Consolas, "Courier New", monospace',
            lineNumbers: 'on',
            folding: true,
            wordWrap: 'on',
            automaticLayout: true,
            padding: { top: 16, bottom: 16 },
            renderWhitespace: 'selection',
            bracketPairColorization: { enabled: true },
            smoothScrolling: true,
            cursorBlinking: 'smooth',
            selectOnLineNumbers: true,
            lineNumbersMinChars: 3,
            glyphMargin: false,
            contextmenu: false, // Disable right-click menu in read-only mode
            quickSuggestions: false,
            parameterHints: { enabled: false },
            suggestOnTriggerCharacters: false,
            acceptSuggestionOnEnter: 'off',
            tabCompletion: 'off',
            hover: { enabled: true }, // Keep hover for better UX
            links: false
          }}
          loading={
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <FileCode className="h-8 w-8 mx-auto mb-2 animate-pulse" />
                <p className="text-sm">Loading editor...</p>
              </div>
            </div>
          }
        />
      </div>

      {/* Status bar */}
      <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-600">
        <div className="flex justify-between items-center">
          <span>{lineCount.toLocaleString()} lines • {fileSize}</span>
          <div className="flex items-center gap-2">
            <span>{detectedLanguage}</span>
            <span>•</span>
            <span>UTF-8</span>
            <span>•</span>
            <span className="text-gray-500">Read-only</span>
          </div>
        </div>
      </div>
    </div>
  )
}